'use es6';

import styled from 'foundations-theming/utils';
import { css } from 'styled-components';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { MoveableObject } from 'foundations-components/transitional/components/MoveableObject';
import { Image } from 'foundations-components/transitional/components/Image';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NewFieldIconMapping, fieldTypesWithIcon } from 'ui-forms-components/constants/propertyIcons';
import { DividerIcon } from 'foundations-assets/icons/components/DividerIcon';
import { GaugeIcon } from 'foundations-assets/icons/components/GaugeIcon';
import { HeaderIcon } from 'foundations-assets/icons/components/HeaderIcon';
import { InsertImageIcon } from 'foundations-assets/icons/components/InsertImageIcon';
import { RichTextIcon } from 'foundations-assets/icons/components/RichTextIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PropertyWithStyles = styled.div.withConfig({
  displayName: "Property__PropertyWithStyles"
})(({
  theme: _theme,
  isDragging
}) => {
  const theme = _theme;
  return css(["margin:", ";white-space:nowrap;", ""], theme.space['100'], isDragging ? `
      background-color: ${theme.color['fill-primary-disabled']};
      opacity: 0.3;
    ` : '');
});
const PropertyRequiredTooltip = ({
  name = '',
  label = ''
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Text, {
      as: "h5",
      children: [label, " ", /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'editor.options.required'
      })]
    }), /*#__PURE__*/_jsx(Text, {
      children: name
    })]
  }),
  children: /*#__PURE__*/_jsx(Text, {
    as: "h5",
    children: "*"
  })
});
const PropertyIcon = ({
  fieldType = ''
}) => {
  if (fieldTypesWithIcon[fieldType]) {
    switch (NewFieldIconMapping[fieldType]) {
      case 'header':
        return /*#__PURE__*/_jsx(HeaderIcon, {});
      case 'richText':
        return /*#__PURE__*/_jsx(RichTextIcon, {});
      case 'insertImage':
        return /*#__PURE__*/_jsx(InsertImageIcon, {});
      case 'divider':
        return /*#__PURE__*/_jsx(DividerIcon, {});
      case 'gauge':
        return /*#__PURE__*/_jsx(GaugeIcon, {});
      default:
        return /*#__PURE__*/_jsx(_Fragment, {});
    }
  }
  if (NewFieldIconMapping[fieldType]) {
    return /*#__PURE__*/_jsx(Image, {
      "data-test-id": "property-icon",
      src: NewFieldIconMapping[fieldType],
      inlineSize: "50px",
      marginBlockStart: "-400"
    });
  }
  return /*#__PURE__*/_jsx(Image, {
    src: NewFieldIconMapping.text,
    width: 50,
    marginBlockStart: "-400"
  });
};
const PropertyLabel = ({
  label = '',
  name = '',
  required = false,
  isFormLabelPropertyEnabled = false,
  children
}) => /*#__PURE__*/_jsxs(Flex, {
  justifyContent: "flex-start",
  children: [/*#__PURE__*/_jsx(Box, {
    shrink: 0,
    children: children
  }), /*#__PURE__*/_jsxs(Box, {
    grow: 1,
    children: [label, isFormLabelPropertyEnabled ? /*#__PURE__*/_jsxs(Text, {
      as: "small",
      children: [" (", name, ")"]
    }) : null]
  }), /*#__PURE__*/_jsx(Box, {
    children: required && /*#__PURE__*/_jsx(PropertyRequiredTooltip, {
      name: name,
      label: label
    })
  })]
});
const PropertyWrapper = ({
  handleClick = () => {},
  children
}) => /*#__PURE__*/_jsx(MoveableObject, {
  "data-test-id": "draggable-property-handle",
  onClick: handleClick,
  closeable: false,
  children: children
});
const PropertyField = ({
  id = '',
  name = '',
  label = '',
  fieldType = '',
  objectTypeId = '',
  disabled = true,
  required = false,
  isBeingDragged = false,
  handleClick = () => {},
  connectDragSource = () => {},
  isFormLabelPropertyEnabled = false
}) => {
  if (!disabled) {
    return connectDragSource( /*#__PURE__*/_jsx("div", {
      id: `property-${id}`,
      "data-test-id": `${objectTypeId}-${name}`,
      children: /*#__PURE__*/_jsx(PropertyWithStyles, {
        isDragging: isBeingDragged,
        children: /*#__PURE__*/_jsx(PropertyWrapper, {
          handleClick: handleClick,
          children: /*#__PURE__*/_jsx(PropertyLabel, {
            name: name,
            label: label,
            required: required,
            isFormLabelPropertyEnabled: isFormLabelPropertyEnabled,
            children: /*#__PURE__*/_jsx(PropertyIcon, {
              fieldType: fieldType
            })
          })
        })
      })
    }));
  }
  return /*#__PURE__*/_jsx("div", {
    id: `property-${id}`,
    "data-test-id": `${objectTypeId}-${name}`,
    children: /*#__PURE__*/_jsx(PropertyWithStyles, {
      isDragging: true,
      children: /*#__PURE__*/_jsx(PropertyWrapper, {
        children: /*#__PURE__*/_jsx(PropertyLabel, {
          name: name,
          label: label,
          required: required,
          isFormLabelPropertyEnabled: isFormLabelPropertyEnabled,
          children: /*#__PURE__*/_jsx(PropertyIcon, {
            fieldType: fieldType
          })
        })
      })
    })
  });
};
export default PropertyField;